import React from "react";
import PageIntro from "../../../components/admin/PageIntro";
import SectionCard from "../../../components/admin/SectionCard";
import StatCard from "../../../components/data-display/StatCard";
import InfoCard from "../../../components/admin/InfoCard";
import { adminTheme } from "../../../components/admin/adminTheme";
import { useAuth } from "../../../hooks/useAuth";
import { useHrDashboard, useHrManagerWorkbench } from "../hooks";
import type {
  HrDashboardResponseDto,
  HrManagerWorkbenchResponseDto,
} from "../hr.types";

const HrDashboardPage: React.FC = () => {
  const { user } = useAuth();
  const staffSubRole = user?.staffSubRole ?? null;

  const isHeadView = staffSubRole === "CEO" || staffSubRole === "HR_HEAD";
  const isManagerView = staffSubRole === "HR_MANAGER";

  const headQuery = useHrDashboard({
    enabled: !isManagerView,
  });
  const managerQuery = useHrManagerWorkbench({
    enabled: isManagerView,
  });

  if (isManagerView) {
    const { data, isLoading, error } = managerQuery;

    if (isLoading) {
      return (
        <PageIntro
          title="HR Manager Workbench"
          message="Loading recruitment and onboarding assignments…"
        />
      );
    }

    if (error || !data) {
      return (
        <PageIntro
          title="HR Manager Workbench"
          message="Unable to load HR dashboard. Please try again."
        />
      );
    }

    const workbench: HrManagerWorkbenchResponseDto = {
      staffTasks: {
        onboardingsToProcess: data.staffTasks?.onboardingsToProcess ?? 0,
        contractsToReview: data.staffTasks?.contractsToReview ?? 0,
        roleChangesPending: data.staffTasks?.roleChangesPending ?? 0,
      },
      caseTasks: {
        newCasesAssigned: data.caseTasks?.newCasesAssigned ?? 0,
        highPriorityCasesAssigned: data.caseTasks?.highPriorityCasesAssigned ?? 0,
        casesNearingSlaBreaches: data.caseTasks?.casesNearingSlaBreaches ?? 0,
      },
      trainingTasks: {
        staffToEnroll: data.trainingTasks?.staffToEnroll ?? 0,
        trainingCompletionsToReview:
          data.trainingTasks?.trainingCompletionsToReview ?? 0,
        overdueMandatoryTrainingFollowUps:
          data.trainingTasks?.overdueMandatoryTrainingFollowUps ?? 0,
      },
    };

    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: adminTheme.space.section,
          padding: adminTheme.space.section,
          background: adminTheme.color.pageBg,
          minHeight: "100%",
        }}
      >
        <PageIntro
          title="HR Manager Workbench"
          message="Assigned hiring, onboarding, and compliance follow-up."
        />

        <SectionCard title="Staff tasks">
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
              gap: adminTheme.space.lg,
            }}
          >
            <StatCard
              label="Onboardings to process"
              value={workbench.staffTasks.onboardingsToProcess}
            />
            <StatCard
              label="Contracts to review"
              value={workbench.staffTasks.contractsToReview}
            />
            <StatCard
              label="Role changes pending"
              value={workbench.staffTasks.roleChangesPending}
            />
          </div>
        </SectionCard>

        <SectionCard title="Case tasks">
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
              gap: adminTheme.space.lg,
            }}
          >
            <StatCard
              label="New cases assigned"
              value={workbench.caseTasks.newCasesAssigned}
            />
            <StatCard
              label="High-priority cases"
              value={workbench.caseTasks.highPriorityCasesAssigned}
            />
            <StatCard
              label="Cases nearing SLA breaches"
              value={workbench.caseTasks.casesNearingSlaBreaches}
            />
          </div>
        </SectionCard>

        <SectionCard title="Training follow-up">
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
              gap: adminTheme.space.lg,
            }}
          >
            <StatCard
              label="Staff to enroll"
              value={workbench.trainingTasks.staffToEnroll}
            />
            <StatCard
              label="Training completions to review"
              value={workbench.trainingTasks.trainingCompletionsToReview}
            />
            <StatCard
              label="Overdue training follow-ups"
              value={workbench.trainingTasks.overdueMandatoryTrainingFollowUps}
            />
          </div>
        </SectionCard>
      </div>
    );
  }

  const { data, isLoading, error } = headQuery;

  if (isLoading) {
    return (
      <PageIntro
        title={isHeadView ? "HR Head Dashboard" : "HR Dashboard"}
        message="Loading HR overview…"
      />
    );
  }

  if (error || !data) {
    return (
      <PageIntro
        title={isHeadView ? "HR Head Dashboard" : "HR Dashboard"}
        message="Unable to load HR dashboard. Please try again."
      />
    );
  }

  const dashboard: HrDashboardResponseDto = {
    kpis: {
      activeStaff: data.kpis?.activeStaff ?? 0,
      pendingOnboardings: data.kpis?.pendingOnboardings ?? 0,
      openHrCases: data.kpis?.openHrCases ?? 0,
      trainingSessionsThisMonth: data.kpis?.trainingSessionsThisMonth ?? 0,
    },
    staffOverview: {
      fullTimeStaff: data.staffOverview?.fullTimeStaff ?? 0,
      partTimeStaff: data.staffOverview?.partTimeStaff ?? 0,
      contractors: data.staffOverview?.contractors ?? 0,
    },
    cases: {
      openCases: data.cases?.openCases ?? 0,
      highPriorityCases: data.cases?.highPriorityCases ?? 0,
      resolvedThisMonth: data.cases?.resolvedThisMonth ?? 0,
    },
    training: {
      upcomingSessions: data.training?.upcomingSessions ?? 0,
      completedSessionsThisMonth: data.training?.completedSessionsThisMonth ?? 0,
      overdueMandatoryTraining: data.training?.overdueMandatoryTraining ?? 0,
    },
  };

  const { kpis, staffOverview, cases, training } = dashboard;

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: adminTheme.space.section,
        padding: adminTheme.space.section,
        background: adminTheme.color.pageBg,
        minHeight: "100%",
      }}
    >
      <PageIntro
        title={isHeadView ? "HR Head Dashboard" : "HR Dashboard"}
        message="Recruitment, staffing, compliance, and staff movements."
      />

      <SectionCard title="HR KPIs">
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
            gap: adminTheme.space.lg,
          }}
        >
          <StatCard
            label="Active staff"
            value={kpis.activeStaff}
            helpText={`Pending onboardings: ${kpis.pendingOnboardings}`}
          />
          <StatCard
            label="Open HR cases"
            value={kpis.openHrCases}
            helpText={`Training sessions this month: ${kpis.trainingSessionsThisMonth}`}
          />
          <StatCard
            label="Pending onboardings"
            value={kpis.pendingOnboardings}
            helpText="Staff joining process in progress"
          />
        </div>
      </SectionCard>

      <SectionCard title="Staff overview">
        <InfoCard
          title="Workforce mix"
          body={`Full-time staff: ${staffOverview.fullTimeStaff}, Part-time staff: ${staffOverview.partTimeStaff}, Contractors: ${staffOverview.contractors}.`}
        />
      </SectionCard>

      <SectionCard title="Compliance & cases">
        <InfoCard
          title="Case management"
          body={`Open cases: ${cases.openCases}, High-priority cases: ${cases.highPriorityCases}, Resolved this month: ${cases.resolvedThisMonth}.`}
        />
      </SectionCard>

      <SectionCard title="Training & onboarding">
        <InfoCard
          title="Training compliance"
          body={`Upcoming sessions: ${training.upcomingSessions}, Completed this month: ${training.completedSessionsThisMonth}, Overdue mandatory training: ${training.overdueMandatoryTraining}.`}
        />
      </SectionCard>
    </div>
  );
};

export default HrDashboardPage;